import { useNavigate, useLocation } from "react-router-dom";
import { User, Users, Award, LogOut, LayoutDashboard, Settings as SettingsIcon } from "lucide-react";
import { useClerk } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import logo from "../../assets/Logo2.png";
import { useSchoolRole, canManageStaff } from "../../hooks/useSchoolRole";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useClerk();
  const { t, i18n } = useTranslation();

  // Staff management is gated at ADMIN+ on the backend. Surface the nav entry
  // unless we positively know the viewer ranks below ADMIN — failing open so a
  // missing/unresolved role (e.g. older API) never hides it from an admin.
  const schoolRole = useSchoolRole();
  const showManageStaff = canManageStaff(schoolRole);

  const navItems = [
    { name: t("sidebar.dashboard", { defaultValue: "Dashboard" }), path: "/dashboard", icon: LayoutDashboard },
    // Engagement moved into Manage Students as a tab (it's entirely per-student).
    { name: t("sidebar.manageStudents"), path: "/manage-students", icon: Users },
    ...(showManageStaff ? [{ name: t("sidebar.manageStaff"), path: "/manage-staff", icon: User }] : []),
    { name: t("sidebar.certificates", { defaultValue: "Certificates" }), path: "/certificates", icon: Award },
    { name: t("sidebar.settings", { defaultValue: "Settings" }), path: "/settings", icon: SettingsIcon },
  ];

  // Spanish + French are hidden for now (translations pending review).
  // Re-enable by uncommenting — the picker auto-shows again once there's
  // more than one option, and the es/fr locale files are still in place.
  const languageOptions = [
    { code: "en", label: t("language.english") },
    // { code: "es", label: t("language.spanish") },
    // { code: "fr", label: t("language.french") },
  ];

  const handleLanguageChange = (language) => {
    void i18n.changeLanguage(language);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    // Relative path — Clerk navigates via window.location after clearing
    // the session, lands us back on the School-Admin sign-in surface.
    signOut({ redirectUrl: "/signin" });
  };

  return (
    <div className="flex flex-col fixed top-0 left-0 min-h-screen w-[230px] bg-blue-50 px-4 pb-3 font-Manrope">
      {/* Logo */}
      <div className="flex justify-center py-8">
        <img src={logo} alt="Logo" className="h-[25px] w-[130px]" />
      </div>

      {/* Nav items */}
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.name}
              type="button"
              onClick={() => handleNavigation(item.path)}
              className={`w-full flex items-center gap-[8px] px-[12px] py-[10px] rounded-[8px] text-[16px] font-medium transition-colors ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "text-gray-600 hover:bg-blue-100"
              }`}
            >
              <item.icon
                size={20}
                className={isActive ? "text-white" : "text-gray-500"}
              />
              <span>{item.name}</span>
            </button>
          );
        })}
      </nav>

      {/* Only render the language picker when there's an actual choice. With
          es/fr hidden, this collapses to nothing rather than showing a lone
          "English" button. */}
      {languageOptions.length > 1 && (
        <div className="mb-4">
          <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t("sidebar.language")}</div>
          <div className="flex items-center gap-2">
            {languageOptions.map((option) => {
              const isActive = i18n.resolvedLanguage?.startsWith(option.code);
              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => handleLanguageChange(option.code)}
                  className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
                    isActive ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-blue-100"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        className="mb-4 flex items-center gap-3 px-3 py-2 rounded-[8px] text-[16px] text-gray-600 font-medium font-['Plus Jakarta Sans'] hover:text-red-500 hover:bg-red-50 transition-colors"
      >
        <LogOut size={18} />
        <span>{t("sidebar.logout")}</span>
      </button>
    </div>
  );
};

export default Sidebar;
